import { groupBy, uniq } from 'lodash';

export function isPivotColumn(col) {
  return 'pivot' in col;
}

/**
 * Expands pivot column entries in a report table configuration into concrete
 * columns derived from the data, and transforms the data rows accordingly.
 *
 * Rows are grouped by all data columns except the pivot key and value columns.
 * This means any column present in the data (including non-displayed columns
 * like UIDs) contributes to row identity, preventing incorrect merging.
 *
 * @param {Array} data - The flat row data from the report view
 * @param {Array} columns - Column configuration array (static + pivot entries)
 * @param {string} [groupByColumn] - Optional column used for row grouping display
 */
export function expandPivotColumns(data, columns, groupByColumn) {
  const pivots = columns.filter(isPivotColumn);

  if (pivots.length === 0) {
    return { columns, data };
  }

  const staticColumns = columns.filter(col => !isPivotColumn(col));
  const staticColumnNames = staticColumns.map(c => c.columnName);

  const pivotCols = pivots.flatMap(p => [p.pivot.keyColumn, p.pivot.valueColumn]);
  const pivotKeyAndValueColumns = new Set(pivotCols);

  const identityColumnNames =
    data.length > 0
      ? Object.keys(data[0]).filter(col => !pivotKeyAndValueColumns.has(col))
      : staticColumnNames;

  const pivotValues = new Map();

  pivots.forEach((pivot) => {
    const filtered = data
      .map(row => row[pivot.pivot.keyColumn])
      .filter(v => v !== null && v !== undefined && v !== '');
    const uniqueKeys = uniq(filtered).sort();

    pivotValues.set(pivot.pivot.keyColumn, uniqueKeys);
  });

  const identityKey = row =>
    identityColumnNames
      .map((col) => {
        const val = row[col];
        return val === null || val === undefined ? '' : String(val);
      })
      .join('\x1F');

  const grouped = groupBy(data, identityKey);

  const pivotedData = Object.values(grouped).map((rows) => {
    const baseRow = {};

    staticColumnNames.forEach((col) => {
      baseRow[col] = rows[0][col];
    });

    identityColumnNames.forEach((col) => {
      if (!(col in baseRow)) {
        baseRow[col] = rows[0][col];
      }
    });

    if (
      groupByColumn &&
      !staticColumnNames.includes(groupByColumn) &&
      !identityColumnNames.includes(groupByColumn)
    ) {
      baseRow[groupByColumn] = rows[0][groupByColumn];
    }

    pivots.forEach((pivot) => {
      const { keyColumn, valueColumn } = pivot.pivot;

      rows.forEach((row) => {
        const pivotKey = row[keyColumn];

        if (pivotKey !== null && pivotKey !== undefined && pivotKey !== '') {
          baseRow[String(pivotKey)] = row[valueColumn];
        }
      });
    });

    return baseRow;
  });

  const expandedColumns = [];

  columns.forEach((col) => {
    if (isPivotColumn(col)) {
      const keys = pivotValues.get(col.pivot.keyColumn) || [];

      keys.forEach((key) => {
        expandedColumns.push({
          columnName: key,
          displayHeader: key,
          type: col.pivot.type === 'dynamic' ? undefined : col.pivot.type,
        });
      });
    } else {
      expandedColumns.push(col);
    }
  });

  return {
    columns: expandedColumns,
    data: pivotedData,
  };
}
